// Page-1 intake form with live scoring (the calc engine runs in the browser — the same code the API
// stores results with) and last year's values beside every field.
import type { AssessmentResult } from '@ngf/calc-engine';
import { FollowUpCard } from '../components/FollowUp.tsx';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { clean, MeasurementFields, Results, scoreLive, VISIT_TYPE_LABEL, type Measurements, type VisitType } from '../components/Intake.tsx';
import { ErrorBox } from '../components/Layout.tsx';
import { PacketPanel } from '../components/Packets.tsx';
import { api, canImport, canRelease, isStaff, patch, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { PhysicianSelect } from '../components/PhysicianSelect.tsx';
import { SignatureBlock, SignPanel, type Review } from './Review.tsx';
import { useData } from '../lib/useData.ts';
import { ConfirmedChecks, useValueChecks, ValueChecksBox, type ConfirmedCheck } from '../components/ValueChecks.tsx';

export type { Measurements };

interface AssessmentDoc {
  id: string; participantId: string; assessedOn: string; status: string; source: string; ageYears: number | null;
  measurements: Measurements; firstName: string; lastName: string; sex: 'M' | 'F' | null; dob: string | null;
  employeeId: string | null; departmentId: string | null; departmentName: string | null; mainDepartmentName: string | null; engineVersion: string | null; results: AssessmentResult | null;
  legacyNotes: string | null; correctsId: string | null; releasedAt: string | null; review: Review | null;
  voidReason: string | null; voidedAt: string | null; services?: VisitService[];
  supersededBy: string | null; paperSigned: boolean | null; correctsPaperSigned: boolean | null; visitType?: VisitType;
  correctsSigner?: string | null; correctsSignatureKind?: string | null; correctsPhysicianId?: string | null;
  correctsDecision?: Decision | null; eventId?: string | null;
  valueChecks?: ConfirmedCheck[] | null;   // unusual values confirmed as correct (2026-10-28)
  previous: { id: string; assessedOn: string; measurements: Measurements; results: AssessmentResult | null } | null;
}

const EDITABLE = ['draft', 'synced', 'reviewed'];
interface Decision { respiratorQualified: boolean | null; referred: boolean | null; referralReason: string | null; notes: string | null }

export function AssessmentEditor() {
  const { id } = useParams();
  const { data, error, reload } = useData<AssessmentDoc>(`/api/assessments/${id}`);
  const [m, setM] = useState<Measurements>({});
  const [assessedOn, setAssessedOn] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saveErr, setSaveErr] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const nav = useNavigate();
  const { me } = useAuth();

  useEffect(() => {
    if (data) {
      setM(data.measurements ?? {});
      setAssessedOn(data.assessedOn);
      setDirty(false);
    }
  }, [data]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const live = useMemo(() => (data ? scoreLive(data.sex, data.dob, assessedOn, m) : null), [data, m, assessedOn]);
  // Value checks (2026-10-28): confirmed ones start ticked; a changed value is a new flag to confirm.
  const checkable = !!data && EDITABLE.includes(data.status) && (data.visitType ?? 'physical') !== 'labs';
  const flags = useValueChecks(checkable ? { participantId: data!.participantId, assessmentId: data!.id, assessedOn, measurements: clean(m) } : null);
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  useEffect(() => { setConfirmed(new Set((data?.valueChecks ?? []).map((c) => c.key))); }, [data]);
  const openFlags = flags.filter((f) => !confirmed.has(f.key)).length;

  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="muted">Loading…</p>;
  const editable = EDITABLE.includes(data.status);
  const prev = data.previous?.measurements ?? {};

  const act = async (what: 'release' | 'correct', importFix = false) => {
    const msg = what === 'release' ? 'Release this signed assessment to the organization portal?'
      : importFix ? 'Fix the import? You will edit a new copy; when you finish it, it keeps the paper signature and replaces this one (no physician review).'
      : 'Create a correction? The signed original stays unchanged; you will edit a new draft that a physician signs.';
    if (!window.confirm(msg)) return;
    setSaveErr(null);
    try {
      const r = await post<{ id?: string }>(`/api/reviews/${data.id}/${what}`, what === 'correct' ? { importFix } : {});
      if (what === 'correct' && r.id) nav(`/assessments/${r.id}`); else await reload();
    } catch (e) { setSaveErr(e); }
  };
  // A correction of a paper-signed import: finish it with the original paper signature (no sign queue).
  // Paper: confirm (or correct) who signed the form, chosen from the list. Staff-completed ones need no physician.
  const finishImportFix = async (signedById: string | undefined, decision: Decision) => {
    if (dirty) { setSaveErr(new Error('Save your changes first.')); return; }
    if (openFlags) { setSaveErr(new Error('Check the highlighted values first (fix them, or tick Correct as entered).')); return; }
    setSaveErr(null);
    try {
      const change = { ...(signedById && signedById !== data.correctsPhysicianId ? { signedById } : {}), decision, confirmChecks: [...confirmed] };
      const r = await post<{ status: string }>(`/api/reviews/${data.id}/finish-import-fix`, change);
      setSaved(`Finished: ${r.status}${data.correctsSignatureKind === 'paper' ? ', with the paper signature' : ''}. The original is replaced.`);
      await reload();
    } catch (e) { setSaveErr(e); }
  };
  const voidIt = async () => {
    if (dirty && !window.confirm('You have unsaved changes; they will be lost. Continue?')) return;
    const reason = window.prompt('Void this assessment? It is kept for the record, hidden from lists, and can be restored later.\n\nReason (required):');
    if (reason === null) return;
    if (reason.trim().length < 3) { setSaveErr(new Error('Give a reason (at least 3 characters) to void.')); return; }
    setSaveErr(null);
    try { await post(`/api/review/assessments/${data.id}/void`, { reason: reason.trim() }); setSaved('Voided.'); await reload(); }
    catch (e) { setSaveErr(e); }
  };
  const restore = async () => {
    if (!window.confirm('Restore this assessment? It goes back to the status it had before it was voided.')) return;
    setSaveErr(null);
    try {
      const r = await post<{ status: string; duplicate: boolean }>(`/api/review/assessments/${data.id}/restore`);
      setSaved(`Restored as ${r.status}.${r.duplicate ? ' This person has another capture at the same event: it is on the Review page as a possible duplicate.' : ''}`);
      await reload();
    } catch (e) { setSaveErr(e); }
  };
  const update = (patchM: Partial<Measurements>) => { setM((cur) => ({ ...cur, ...patchM })); setDirty(true); setSaved(null); };
  const save = async (status?: string) => {
    setSaveErr(null);
    try {
      await patch(`/api/assessments/${data.id}`, { measurements: clean(m), assessedOn, ...(status ? { status } : {}),
        ...(status === 'reviewed' ? { confirmChecks: [...confirmed] } : {}) });
      setDirty(false);
      setSaved(status ? `Saved and marked ${status}.` : 'Saved.');
      await reload();
    } catch (e) { setSaveErr(e); }
  };

  const visitType: VisitType = data.visitType ?? 'physical';
  const changeType = async (t: VisitType) => {
    if (t === visitType) return;
    if (dirty) { setSaveErr(new Error('Save your changes first.')); return; }
    setSaveErr(null);
    try { await patch(`/api/assessments/${data.id}`, { visitType: t }); setSaved(`Changed to ${VISIT_TYPE_LABEL[t]}.`); await reload(); }
    catch (e) { setSaveErr(e); }
  };

  return (
    <>
      <p className="no-print"><Link to={`/participants/${data.participantId}`}>← {data.lastName}, {data.firstName}</Link></p>
      <div className="card spread">
        <div>
          <h1 style={{ margin: 0 }}>{data.lastName}, {data.firstName} <span className="badge visit-type">{VISIT_TYPE_LABEL[visitType]}</span></h1>
          <div className="muted">
            {data.sex === 'M' ? 'Male' : data.sex === 'F' ? 'Female' : 'Sex not recorded'} · DOB {data.dob ?? '—'}
            {live ? ` · age ${live.age.toFixed(2)}` : ''} · ID # {data.employeeId ?? '—'}
          </div>
          <TestedWith assessmentId={data.id} departmentId={data.departmentId} departmentName={data.departmentName}
            mainName={data.mainDepartmentName} editable={editable && !dirty} onChange={() => void reload()} />
        </div>
        <div className="actions no-print">
          <span className={`badge status ${data.status}`}>{data.status}</span>
          {editable && <button className="secondary" onClick={() => void save()} disabled={!dirty}>Save</button>}
          {editable && data.status !== 'reviewed' && <button onClick={() => void save('reviewed')} disabled={openFlags > 0}
            title={openFlags ? 'Check the highlighted values first' : undefined}>Save &amp; mark reviewed</button>}
          {data.status === 'signed' && me && canRelease(me.role) &&
            <button onClick={() => void act('release')}>Release to organization</button>}
          {['signed', 'released'].includes(data.status) && !data.supersededBy && me && isStaff(me.role) &&
            <button className="secondary" onClick={() => data.paperSigned ? setCorrecting(!correcting) : void act('correct')}>Create correction</button>}
          <button className="secondary" onClick={() => nav(`/assessments/${data.id}/report`)}>Report</button>
          {editable && me && isStaff(me.role) && <button className="secondary danger" onClick={() => void voidIt()}>Void</button>}
          {data.status === 'void' && me && isStaff(me.role) && <button className="secondary" onClick={() => void restore()}>Restore</button>}
        </div>
      </div>
      {data.status !== 'void' && <ServicesCard assessmentId={data.id} services={data.services ?? []} />}
      {correcting && !data.supersededBy && <div className="card no-print">
        <h3 style={{ marginTop: 0 }}>What kind of correction?</h3>
        <p className="muted">This assessment was imported from a form the physician signed on paper.</p>
        <div className="actions">
          <button onClick={() => void act('correct', true)}>The import was wrong (paper form already signed)</button>
          <button className="secondary" onClick={() => void act('correct', false)}>The results changed (physician must sign)</button>
          <button className="secondary" onClick={() => setCorrecting(false)}>Cancel</button>
        </div>
        <p className="muted small">Import mistakes (a wrong date, a misread value) are finished without the sign queue and keep the paper signature.</p>
      </div>}
      {editable && data.correctsId && <div className="card no-print"><CorrectionMoves a={data} dirty={dirty} onChange={() => void reload()} /></div>}
      {data.supersededBy && <div className="alert info">This version was replaced by <Link to={`/assessments/${data.supersededBy}`}>a correction</Link>. It is kept for the record; lists, summaries and reports use the correction.</div>}
      {data.correctsId && <div className="alert info">This is a correction of <Link to={`/assessments/${data.correctsId}`}>a signed assessment</Link>.
        {editable && data.correctsPaperSigned && ' The original was signed on paper: if only the import was wrong, fix it here and use "Finish correction" at the bottom of the page; otherwise mark it reviewed for the physician.'}</div>}
      {editable && (data.status !== 'reviewed' || dirty || !!data.correctsId)
        ? <ValueChecksBox flags={flags} confirmed={confirmed} onChange={setConfirmed} />
        : <ConfirmedChecks checks={data.valueChecks} />}
      {data.review && <SignatureBlock review={data.review} />}
      {['signed', 'released'].includes(data.status) && <FollowUpCard assessmentId={data.id} />}
      {data.status === 'reviewed' && me?.role === 'physician' && !dirty &&
        <SignPanel assessmentId={data.id} me={me} initial={data.correctsDecision ?? undefined} onSigned={() => void reload()} />}
      {['signed', 'released'].includes(data.status) && <PacketPanel key={data.status} assessmentId={data.id} />}
      <ErrorBox error={saveErr} />
      {saved && <div className="alert ok">{saved}</div>}
      {dirty && <div className="alert info no-print">Unsaved changes.</div>}
      {data.status === 'void'
        ? <div className="alert error">Voided {data.voidedAt ? new Date(data.voidedAt).toLocaleString() : ''}: {data.voidReason}. Kept for the record; it no longer appears in lists and cannot be changed. Use Restore to bring it back.</div>
        : !editable && <div className="alert info">This assessment is {data.status} and can no longer be edited.</div>}
      {(!data.sex || !data.dob) && <div className="alert info">Add the participant's date of birth and sex to calculate results.</div>}
      {data.legacyNotes && <div className="alert info"><strong>ePhysioLogic notes:</strong> {data.legacyNotes}</div>}

      <div className="grid2">
        <div>
          <fieldset><legend>Visit</legend>
            <div className="row">
              <div className="field"><label>Assessment date</label>
                <input type="date" value={assessedOn} disabled={!editable} onChange={(e) => { setAssessedOn(e.target.value); setDirty(true); }} /></div>
              <div className="field" style={{ flex: '0 1 200px' }}><label htmlFor="visit-type">Type</label>
                <select id="visit-type" value={visitType} disabled={!editable} onChange={(e) => void changeType(e.target.value as VisitType)}>
                  {(['physical', 'cohsg', 'labs'] as const).map((t) => <option key={t} value={t}>{VISIT_TYPE_LABEL[t]}</option>)}
                </select></div>
              <div className="field muted small" style={{ alignSelf: 'center' }}>
                {data.previous ? <>Previous assessment: <Link to={`/assessments/${data.previous.id}`}>{data.previous.assessedOn}</Link></> : 'No previous assessment on file.'}
              </div>
            </div>
          </fieldset>

          {visitType === 'labs'
            ? <LabsVisit participantId={data.participantId} assessmentId={data.id} />
            : <MeasurementFields m={m} update={update} editable={editable} sex={data.sex} prev={prev} visitType={visitType} />}
        </div>

        {visitType !== 'labs' && <div className="sticky">
          {/* Editable records score live; signed, released and imported records show what was issued. */}
          <Results live={editable ? live?.r ?? null : data.results} prev={data.previous?.results ?? null} prevDate={data.previous?.assessedOn}
            label={editable ? 'calculated live' : data.source === 'legacy_epl' ? 'as issued (ePhysioLogic)' : 'as signed'} visitType={visitType} />
        </div>}
      </div>
      {editable && data.correctsId && <div id="correction-card" className="card no-print">
        {data.correctsPaperSigned && me && canImport(me.role)
          ? <FinishPaperFix paper={data.correctsSignatureKind === 'paper'} currentId={data.correctsPhysicianId ?? ''} currentName={data.correctsSigner ?? ''}
              decision={data.correctsDecision ?? { respiratorQualified: null, referred: null, referralReason: null, notes: null }}
              onFinish={(id, d) => void finishImportFix(id, d)} />
          : <OriginalDecisions d={data.correctsDecision ?? null} paper={!!data.correctsPaperSigned} />}
      </div>}
    </>
  );
}

/** Finishing an import-fix correction of a paper-signed form: who signed it (change it if the import recorded the wrong one). */
/** Finishing an import fix: who signed the paper form (paper only), and the decisions as written on it, both corrected here
 *  when the import recorded them wrong. Filled in from the original. */
function FinishPaperFix({ paper, currentId, currentName, decision, onFinish }: {
  paper: boolean; currentId: string; currentName: string; decision: Decision; onFinish: (physicianId: string | undefined, d: Decision) => void;
}) {
  const [md, setMd] = useState(currentId);
  const [d, setD] = useState<Decision>(decision);
  const yn = (v: boolean | null) => (v === null ? '' : v ? 'yes' : 'no');
  const parse = (v: string) => (v === '' ? null : v === 'yes');
  const changed = (k: keyof Decision) => (d[k] ?? null) !== (decision[k] ?? null);
  const tag = (k: keyof Decision) => changed(k) ? <span className="badge reviewed" style={{ marginLeft: 6 }}>changed</span> : null;
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Physician's decisions</h3>
      <p className="small muted" style={{ marginTop: 0 }}>Filled in from the original: change anything the import recorded wrong. <b>Finish correction</b> saves them,
        {paper ? ' keeps the paper signature' : ' completes it without a physician signature, as the original was'}, replaces the original and makes a new report packet.</p>
      {paper && <div className="field" style={{ maxWidth: 380 }}><label htmlFor="fix-md">Physician who signed the paper form</label>
        <PhysicianSelect id="fix-md" value={md} onChange={setMd} />
        <div className="hint">On record: {currentName || '—'}. Change it if the import recorded the wrong physician.</div></div>}
      <div className="row">
        <div className="field" style={{ flex: '0 1 220px' }}><label htmlFor="fix-rq">Qualified to use a respirator {tag('respiratorQualified')}</label>
          <select id="fix-rq" value={yn(d.respiratorQualified)} onChange={(e) => setD({ ...d, respiratorQualified: parse(e.target.value) })}>
            <option value="">Not recorded</option><option value="yes">Yes</option><option value="no">No</option></select></div>
        <div className="field" style={{ flex: '0 1 220px' }}><label htmlFor="fix-rf">Referred {tag('referred')}</label>
          <select id="fix-rf" value={yn(d.referred)} onChange={(e) => setD({ ...d, referred: parse(e.target.value) })}>
            <option value="">Not recorded</option><option value="yes">Yes</option><option value="no">No</option></select></div>
      </div>
      {d.referred && <div className="field"><label htmlFor="fix-rr">Reason for the referral {tag('referralReason')}</label>
        <input id="fix-rr" value={d.referralReason ?? ''} maxLength={1000} onChange={(e) => setD({ ...d, referralReason: e.target.value || null })} /></div>}
      <div className="field"><label htmlFor="fix-no">Physician notes {tag('notes')}</label>
        <textarea id="fix-no" rows={2} value={d.notes ?? ''} maxLength={2000} onChange={(e) => setD({ ...d, notes: e.target.value || null })} /></div>
      <div className="actions">
        <button disabled={(paper && !md) || (!!d.referred && !d.referralReason?.trim())} onClick={() => onFinish(paper ? md : undefined, d)}>Finish correction</button></div>
    </div>);
}

interface EventOpt { id: string; departmentName: string; startsOn: string; endsOn: string | null; location: string | null }
interface FoundPerson { id: string; firstName: string; lastName: string; dob: string | null; employeeId: string | null; departmentName: string | null }

/** A correction can also move the visit to the right person (it was recorded on someone else) or the right event. */
/** A correction the physician signs: their decisions on the original (the sign panel starts from them). */
function OriginalDecisions({ d, paper }: { d: Decision | null; paper: boolean }) {
  const yn = (v: boolean | null | undefined) => (v === null || v === undefined ? 'not recorded' : v ? 'Yes' : 'No');
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Physician's decisions</h3>
      {d ? <p className="small" style={{ margin: '0.25rem 0' }}>On the original: qualified to use a respirator <b>{yn(d.respiratorQualified)}</b>; referred <b>{yn(d.referred)}</b>
        {d.referred && d.referralReason ? <> ({d.referralReason})</> : null}{d.notes ? <>; notes: {d.notes}</> : null}.</p> : null}
      <p className="small muted" style={{ margin: 0 }}>{paper ? 'Technicians and admins change these here when finishing the correction.'
        : 'The physician confirms or changes these when signing this correction (the sign panel starts from the original).'} Mark it reviewed to send it to the sign queue.</p>
    </div>);
}

function CorrectionMoves({ a, dirty, onChange }: { a: AssessmentDoc; dirty: boolean; onChange: () => void }) {
  const [open, setOpen] = useState<'person' | 'event' | null>(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<FoundPerson[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const events = useData<EventOpt[]>(open === 'event' || a.eventId ? '/api/events' : null);
  const ev = events.data?.find((e) => e.id === a.eventId);
  const label = (e: EventOpt) => `${e.startsOn}${e.endsOn && e.endsOn !== e.startsOn ? ` – ${e.endsOn}` : ''} · ${e.departmentName}${e.location ? ` · ${e.location}` : ''}`;
  // Events within 120 days of the test date, nearest first.
  const near = (events.data ?? []).map((e) => ({ e, gap: Math.abs(Date.parse(e.startsOn) - Date.parse(a.assessedOn)) / 864e5 }))
    .filter((x) => x.gap <= 120).sort((x, y) => x.gap - y.gap).map((x) => x.e);
  const search = async () => {
    setErr(null);
    try { setFound((await api<FoundPerson[]>('GET', `/api/participants?limit=15&q=${encodeURIComponent(q.trim())}`)).filter((p) => p.id !== a.participantId)); } catch (x) { setErr(x); }
  };
  const move = async (body: object, confirmText: string) => {
    if (!window.confirm(confirmText)) return;
    setErr(null);
    try { await patch(`/api/assessments/${a.id}`, body); setOpen(null); onChange(); } catch (x) { setErr(x); }
  };
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Correction: person and event</h3>
      <p className="small" style={{ margin: '0.25rem 0' }}><b>{a.lastName}, {a.firstName}</b> · DOB {a.dob ?? '—'} · ID {a.employeeId ?? '—'}{' '}
        {!dirty && <button className="link" onClick={() => setOpen(open === 'person' ? null : 'person')}>Wrong person…</button>}</p>
      <p className="small" style={{ margin: '0.25rem 0' }}>Event: {a.eventId ? (ev ? label(ev) : '…') : 'none'}{' '}
        {!dirty && <button className="link" onClick={() => setOpen(open === 'event' ? null : 'event')}>Change event…</button>}</p>
      {dirty && <p className="small muted" style={{ margin: 0 }}>Save your changes to move it to another person or event.</p>}
      <ErrorBox error={err} />
      {open === 'person' && <>
        <p className="small muted">The visit was recorded on the wrong person: choose the right one. When this correction is finished, the original
          disappears from this person's history and the corrected visit is on the person you choose. Results are recalculated for their age and sex.</p>
        <div className="row" style={{ alignItems: 'end' }}>
          <div className="field"><label htmlFor="cm-q">Find the right person</label>
            <input id="cm-q" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }} placeholder="Name or employee ID" /></div>
          <div style={{ flex: '0 0 auto' }}><button type="button" className="secondary" disabled={!q.trim()} onClick={() => void search()}>Search</button></div>
        </div>
        {found.map((p) => (
          <div key={p.id} className="small" style={{ margin: '4px 0' }}>
            <b>{p.lastName}, {p.firstName}</b> · DOB {p.dob ?? '—'} · ID {p.employeeId ?? '—'} · {p.departmentName ?? 'No organization'}{' '}
            <button className="link" onClick={() => void move({ participantId: p.id }, `Move this correction to ${p.lastName}, ${p.firstName}?`)}>Move it here</button>
          </div>))}
      </>}
      {open === 'event' && <>
        <p className="small muted">Events within 120 days of the test date ({a.assessedOn}), nearest first.</p>
        {!events.data ? <p className="muted small">Loading…</p> : <>
          {near.map((e) => (
            <div key={e.id} className="small" style={{ margin: '4px 0' }}>{label(e)}{' '}
              {e.id === a.eventId ? <span className="muted">(current)</span>
                : <button className="link" onClick={() => void move({ eventId: e.id }, `Put this correction on the event ${label(e)}?`)}>Use this event</button>}
            </div>))}
          {!near.length && <p className="muted small">No events near that date.</p>}
          {a.eventId && <button className="link small" onClick={() => void move({ eventId: null }, 'Take this correction off its event?')}>Not at an event</button>}
        </>}
      </>}
    </div>
  );
}

/** A labs-only visit: no fitness form. Lab results come from the LabCorp report, filed through Imports (read value by value)
 *  or uploaded to the person's documents; the lab tests on the visit are ticked on the Services card above. */
function LabsVisit({ participantId, assessmentId }: { participantId: string; assessmentId: string }) {
  const labs = useData<{ id: string; performedOn: string; labName: string | null; serviceName: string; assessmentId: string | null; results: unknown[]; checked: boolean }[]>(
    `/api/participants/${participantId}/labs`);
  const mine = (labs.data ?? []).filter((r) => r.assessmentId === assessmentId);
  return (
    <fieldset><legend>Lab results</legend>
      <p className="small" style={{ marginTop: 0 }}>Labs only: there is no fitness form. Tick the lab tests ordered on the <b>Services</b> card above.
        When the LabCorp report comes back, scan it into <Link to="/imports">Imports</Link> (each value is read and recorded on this person),
        or upload it on the <Link to={`/participants/${participantId}`}>person's page</Link> under Documents.</p>
      {labs.error ? <ErrorBox error={labs.error} />
        : !mine.length ? <p className="muted small">No lab results recorded for this visit yet.</p>
        : <ul className="small">{mine.map((r) => (
            <li key={r.id}>{r.performedOn} · {r.labName ?? 'Lab'} · {r.serviceName} · {r.results.length} values{r.checked ? '' : ' (not yet checked)'}
              {' '}· <Link to={`/participants/${participantId}`}>see Lab results</Link></li>))}</ul>}
    </fieldset>
  );
}

// ---------------------------------------------------------------- worklist
interface WorkRow { id: string; assessedOn: string; status: string; participantId: string; firstName: string; lastName: string; departmentName: string | null }

export function Worklist() {
  const [status, setStatus] = useState('');
  const { data, error } = useData<WorkRow[]>(`/api/assessments?limit=200${status ? `&status=${status}` : ''}`);
  const nav = useNavigate();
  return (
    <>
      <h1>Assessments</h1>
      <div className="card row">
        <div style={{ flex: '0 1 240px' }}><label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option><option value="draft">Draft</option><option value="synced">Synced from iPad</option>
            <option value="reviewed">Reviewed</option><option value="signed">Signed</option><option value="released">Released</option><option value="void">Void</option>
          </select></div>
        <div className="muted small" style={{ alignSelf: 'center' }}>To start a new assessment, open the participant and choose "New assessment".</div>
      </div>
      <ErrorBox error={error} />
      <div className="card">
        {!data?.length ? <p className="muted">Nothing here yet.</p> : (
          <table><thead><tr><th>Date</th><th>Participant</th><th>Organization</th><th>Status</th></tr></thead>
            <tbody>{data.map((r) => (
              <tr key={r.id} className="link" onClick={() => nav(`/assessments/${r.id}`)}>
                <td>{r.assessedOn}</td><td>{r.lastName}, {r.firstName}</td><td>{r.departmentName ?? '—'}</td>
                <td><span className={`badge ${r.status}`}>{r.status}</span></td></tr>))}</tbody></table>
        )}
      </div>
    </>
  );
}

/** The organization this assessment was done for ("tested with"); changeable until it is signed. */
function TestedWith({ assessmentId, departmentId, departmentName, mainName, editable, onChange }: {
  assessmentId: string; departmentId: string | null; departmentName: string | null; mainName: string | null; editable: boolean; onChange: () => void;
}) {
  const [changing, setChanging] = useState(false);
  const depts = useData<{ id: string; name: string }[]>(changing ? '/api/departments' : null);
  const [error, setError] = useState<unknown>(null);
  const pick = async (id: string) => {
    if (!id || id === departmentId) { setChanging(false); return; }
    setError(null);
    try { await patch(`/api/assessments/${assessmentId}`, { departmentId: id }); setChanging(false); onChange(); } catch (e) { setError(e); }
  };
  return (
    <div className="muted">
      Tested with <b>{departmentName ?? 'no organization'}</b>
      {mainName && mainName !== departmentName && <> · main organization {mainName}</>}
      {editable && !changing && <> · <button type="button" className="linkish no-print" onClick={() => setChanging(true)}>change</button></>}
      {changing && (
        <select key={depts.data ? "loaded" : "loading"} autoFocus defaultValue={departmentId ?? ''} onChange={(e) => void pick(e.target.value)} onBlur={() => setChanging(false)} style={{ marginLeft: 8, maxWidth: 320 }}>
          <option value="">{depts.data ? 'Choose…' : 'Loading…'}</option>
          {depts.data?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>)}
      <ErrorBox error={error} />
    </div>
  );
}

interface VisitService { code: string; name: string; category: string; status: 'ordered' | 'done' | 'not_done'; note: string | null; fromForm: boolean }
interface CatalogService { code: string; name: string; category: string; description: string | null }
const CATEGORY_LABEL: Record<string, string> = { fitness: 'Fitness testing', screening: 'Screenings', lab: 'Labs', exam: 'Exams', imaging: 'Imaging' };
const STATUS_LABEL: Record<string, string> = { ordered: 'Ordered', done: 'Done', not_done: 'Not done' };

/** Services on this visit (free choice): what was ordered, done or not done. Values on the form count as done. */
function ServicesCard({ assessmentId, services: initial }: { assessmentId: string; services: VisitService[] }) {
  const [services, setServices] = useState(initial);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const catalog = useData<CatalogService[]>(adding ? '/api/services' : null);
  useEffect(() => setServices(initial), [initial]);
  const set = async (code: string, status: VisitService['status'] | null) => {
    setErr(null);
    try { setServices((await api<{ services: VisitService[] }>('PUT', `/api/assessments/${assessmentId}/services/${code}`, { status })).services); }
    catch (x) { setErr(x); }
  };
  const have = new Set(services.map((s) => s.code));
  const groups = [...new Set((catalog.data ?? []).map((c) => c.category))];
  return (
    <div className="card no-print">
      <div className="spread">
        <h2 style={{ margin: 0 }}>Services on this visit</h2>
        {!adding && <button className="secondary" onClick={() => setAdding(true)}>Add service</button>}
      </div>
      <ErrorBox error={err} />
      {!services.length ? <p className="muted">None recorded yet.</p> : (
        <table style={{ marginTop: '0.5rem' }}>
          <tbody>{services.map((s) => (
            <tr key={s.code}>
              <td>{s.name}<div className="muted small">{CATEGORY_LABEL[s.category] ?? s.category}</div></td>
              <td>{s.fromForm ? <span className="muted small">Done (from the form)</span> : (
                <select aria-label={`Status of ${s.name}`} value={s.status} onChange={(e) => void set(s.code, e.target.value as VisitService['status'])}>
                  {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>)}</td>
              <td style={{ textAlign: 'right' }}>{!s.fromForm && <button className="link" onClick={() => void set(s.code, null)}>Remove</button>}</td>
            </tr>))}</tbody>
        </table>)}
      {adding && (
        <div className="services-picker">
          {!catalog.data ? <p className="muted">Loading…</p> : groups.map((g) => (
            <div key={g}>
              <h3 className="small">{CATEGORY_LABEL[g] ?? g}</h3>
              <div className="chips">{catalog.data!.filter((c) => c.category === g && !have.has(c.code)).map((c) => (
                <button key={c.code} className="chip" title={c.description ?? ''} onClick={() => void set(c.code, 'done')}>+ {c.name}</button>))}</div>
            </div>))}
          <p className="muted small">Added as <b>Done</b>; change to Ordered while waiting for results (e.g. labs).</p>
          <button className="secondary" onClick={() => setAdding(false)}>Close</button>
        </div>)}
    </div>
  );
}
